import { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { MoreVertical, PlusCircle, Eye, Monitor, Smartphone } from "react-feather";
import {
  Col,
  Row,
  Button,
  Container,
  CardBody,
  Card,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Loader from "../../../Loader/Loader";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import moment from "moment";
import { productBaseURL } from '../../../../Services/api/baseURL';
import useRbac from "../../../../Hooks/useRbac";

const SpecialTable = () => {
  const { can } = useRbac();
  const [isLoading, setIsLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bannerModal, setBannerModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const navigate = useNavigate();

  const handleNavigateToAddOffer = () => {
    navigate("/special/create");
  };

  const handleEditOffer = (offerId) => {
    navigate(`/special/edit/${offerId}`);
  };

  const handleViewOffer = (offerId) => {
    navigate(`/special/view/${offerId}`);
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.delete(`${productBaseURL}/products/specials/${offerId}`, {
        headers: { Authorization: `${token}` },
      });

      if (response.data.success) {
        toast.success("Offer deleted successfully!");
        fetchOffers();
      } else {
        toast.error(response.data.message || "Failed to delete offer!");
      }
    } catch (error) {
      console.error("Delete error:", error);
      const errorMessage = error.response?.data?.message || "Failed to delete offer!";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewBanner = (bannerData, offerName) => {
    setSelectedBanner({ ...bannerData, name: offerName });
    setBannerModal(true);
    setActiveTab('1');
  };

  const closeBannerModal = () => {
    setBannerModal(false);
    setSelectedBanner(null);
    setActiveTab('1');
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const offerCols = [
    {
      name: "Offer Name",
      width: "160px",
      selector: (row) => row?.name || "N/A",
      sortable: true,
      sortField: "name",
      cell: (row) => (
        <span
          style={{ fontWeight: "500", color: "#007bff", cursor: "pointer" }}
          onClick={() => handleViewOffer(row._id)}
          title="View offer details"
        >
          {row?.name || "N/A"}
        </span>
      ),
    },
    {
      name: "Banners",
      width: "140px",
      selector: (row) => row?.bannerImages,
      cell: (row) => {
        const hasWebsite = row?.bannerImages?.website;
        const hasMobile = row?.bannerImages?.mobile;

        return (
          <div className="d-flex align-items-center justify-content-center flex-column">
            {(hasWebsite || hasMobile) ? (
              <div className="banner-preview-container d-flex flex-column align-items-center">
                {/* Website Banner Preview */}
                {hasWebsite && (
                  <div className="mb-1 d-flex align-items-center">
                    <Monitor size={12} className="me-1" />
                    <img
                      src={hasWebsite}
                      alt="Website Banner"
                      style={{
                        width: "60px",
                        height: "6px",
                        objectFit: "cover",
                        border: "1px solid #007bff",
                        borderRadius: "2px",
                        cursor: "pointer"
                      }}
                      onClick={() => handleViewBanner(row.bannerImages, row.name)}
                      title="Click to view banners"
                    />
                  </div>
                )}

                {/* Mobile Banner Preview */}
                {hasMobile && (
                  <div className="mb-1 d-flex align-items-center">
                    <Smartphone size={12} className="me-1" />
                    <img
                      src={hasMobile}
                      alt="Mobile Banner"
                      style={{
                        width: "40px",
                        height: "22px",
                        objectFit: "cover",
                        border: "1px solid #28a745",
                        borderRadius: "2px",
                        cursor: "pointer"
                      }}
                      onClick={() => handleViewBanner(row.bannerImages, row.name)}
                      title="Click to view banners"
                    />
                  </div>
                )}

                <Button
                  size="sm"
                  color="link"
                  className="p-0 mt-1"
                  onClick={() => handleViewBanner(row.bannerImages, row.name)}
                  title="View banners"
                >
                  <Eye size={12} />
                </Button>
              </div>
            ) : (
              <span className="text-muted" style={{ fontSize: "11px" }}>
                No Banners
              </span>
            )}
          </div>
        );
      },
    },
    {
      name: "Description",
      width: "160px",
      selector: (row) => row?.description,
      cell: (row) => (
        <span title={row?.description}>
          {row?.description ?
            (row.description.length > 40 ?
              `${row.description.substring(0, 40)}...` :
              row.description
            ) : "N/A"
          }
        </span>
      ),
    },
    {
      name: "Validity",
      width: "120px",
      selector: (row) => row?.validFrom,
      sortable: true,
      sortField: "validFrom",
      cell: (row) => (
        <span>
          {row?.validFrom ? moment(row.validFrom).format("DD MMM YYYY") : "N/A"} -{" "}
          {row?.validTo ? moment(row.validTo).format("DD MMM YYYY") : "N/A"}
        </span>
      ),
    },
    {
      name: "Products",
      selector: (row) => row?.products,
      // width: "0px",
      cell: (row) =>
        row?.products?.length > 0 ? (
          <div className="d-flex flex-wrap">
            {row.products.slice(0, 2).map((p, idx) => {
              const productName = p?.product?.productName || "Unknown";
              const variantName = p?.variant?.variantName;
              const label = variantName
                ? `${productName} - ${variantName}`
                : productName;
              return (
                <Badge
                  key={idx}
                  color="info"
                  pill
                  className="me-1 mb-1"
                  style={{ fontSize: "10px" }}
                  title={label}
                >
                  {label}
                </Badge>
              );
            })}
            {row.products.length > 2 && (
              <Badge
                color="secondary"
                pill
                className="me-1 mb-1"
                style={{ fontSize: "10px" }}
              >
                +{row.products.length - 2}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-muted">No Products</span>
        ),
    },
    {
      name: "Status",
      width: "100px",
      selector: (row) => row?.status,
      sortable: true,
      sortField: "status",
      cell: (row) => (
        <Badge
          color={row.status === "active" ? "success" : "secondary"}
          pill
          className="px-2"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      name: "Created",
      width: "120px",
      selector: (row) => row?.createdAt,
      sortable: true,
      sortField: "createdAt",
      cell: (row) => (
        <span>
          {row?.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "N/A"}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "100px",
      omit: !can('items.update') && !can('items.delete'),
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown mx-2">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleViewOffer(row._id)}>
              View Offer
            </DropdownItem>
            {can('items.update') && <DropdownItem onClick={() => handleEditOffer(row._id)}>
              Edit Offer
            </DropdownItem>}
            {(row?.bannerImages?.website || row?.bannerImages?.mobile) && (
              <DropdownItem onClick={() => handleViewBanner(row.bannerImages, row.name)}>
                View Banners
              </DropdownItem>
            )}
            {can('items.delete') && <DropdownItem onClick={() => handleDeleteOffer(row._id)}>
              Delete
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      center: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const fetchOffers = async () => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        status: statusFilter,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      Object.keys(params).forEach(key => {
        if (params[key] === "" || params[key] === "all") {
          delete params[key];
        }
      });

      const response = await axios.get(`${productBaseURL}/products/specials`, {
        headers: { Authorization: `${token}` },
        params: params
      });
      if (response.data.success) {
        const { data, pagination } = response.data;
        setOffers(data || []);
        setTotalRows(pagination?.totalCount || 0);
      } else {
        toast.error("Failed to fetch special offers!");
        setOffers([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch special offers!";
      toast.error(errorMessage);
      setOffers([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayedFetch = setTimeout(() => {
      fetchOffers();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(delayedFetch);
  }, [currentPage, rowsPerPage, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, sortDirection) => {
    setSortBy(column.sortField || column.selector);
    setSortOrder(sortDirection);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  return (
    <Fragment>
      <Container fluid={true} style={{ paddingTop: "30px" }}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody style={{ padding: "15px" }}>
                <Row className="pb-2">
                  <Col md={4}>
                    <h5 className="mb-0 font-bold">Special Package</h5>
                  </Col>
                  <Col md={8}>
                    <div className="file-content file-content1 justify-content-end">
                      <div className="d-flex align-items-center me-3">
                        <Button
                          size="sm"
                          color={statusFilter === "all" ? "primary" : "outline-primary"}
                          className="me-1"
                          onClick={() => handleStatusFilter("all")}
                        >
                          All
                        </Button>
                        <Button
                          size="sm"
                          color={statusFilter === "active" ? "success" : "outline-success"}
                          className="me-1"
                          onClick={() => handleStatusFilter("active")}
                        >
                          Active
                        </Button>
                        <Button
                          size="sm"
                          color={statusFilter === "inactive" ? "secondary" : "outline-secondary"}
                          onClick={() => handleStatusFilter("inactive")}
                        >
                          Inactive
                        </Button>
                      </div>

                      <div className="mb-0 form-group position-relative search_outer d-flex align-items-center">
                        <i className="fa fa-search" style={{ top: "unset" }}></i>
                        <input
                          className="form-control border-0"
                          style={{ maxWidth: "195px" }}
                          value={searchTerm}
                          onChange={handleSearch}
                          type="text"
                          placeholder="Search offers..."
                        />
                      </div>

                      {can('items.create') && <Button
                        className="btn btn-primary d-flex align-items-center ms-3"
                        onClick={handleNavigateToAddOffer}
                      >
                        <PlusCircle size={16} className="me-1" />
                        Add Offer
                      </Button>}
                    </div>
                  </Col>
                </Row>
              </CardBody>

              <DataTable
                data={offers}
                columns={offerCols}
                striped
                center
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerPageChange}
                onSort={handleSort}
                sortServer
                progressComponent={<Loader />}
                progressPending={isLoading}
                noDataComponent={
                  <div className="text-center py-4">
                    <p className="mb-0">No special offers found</p>
                    {searchTerm && (
                      <small className="text-muted">
                        Try adjusting your search or filter criteria
                      </small>
                    )}
                  </div>
                }
                paginationComponentOptions={{
                  rowsPerPageText: 'Rows per page:',
                  rangeSeparatorText: 'of',
                  selectAllRowsItem: true,
                  selectAllRowsItemText: 'All',
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Dual Banner Modal */}
        <Modal
          isOpen={bannerModal}
          toggle={closeBannerModal}
          size="xl"
          centered
        >
          <ModalHeader toggle={closeBannerModal}>
            Banner Images - {selectedBanner?.name}
          </ModalHeader>
          <ModalBody>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === '1' ? 'active' : ''}
                  onClick={() => toggle('1')}
                  style={{ cursor: 'pointer' }}
                >
                  <Monitor size={16} className="me-2" />
                  Website Banner (11:1)
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === '2' ? 'active' : ''}
                  onClick={() => toggle('2')}
                  style={{ cursor: 'pointer' }}
                >
                  <Smartphone size={16} className="me-2" />
                  Mobile Banner (16:9)
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="mt-3">
              <TabPane tabId="1">
                <div className="text-center">
                  {selectedBanner?.website ? (
                    <div className="banner-full-view">
                      <img
                        src={selectedBanner.website}
                        alt="Website Banner"
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          height: "auto",
                          border: "2px solid #007bff",
                          borderRadius: "8px",
                          aspectRatio: "11/1",
                          objectFit: "cover"
                        }}
                      />
                      <div className="mt-3">
                        <small className="text-muted">
                          <Monitor size={14} className="me-1" />
                          Website Banner - Aspect Ratio: 11:1
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <Monitor size={48} className="text-muted mb-3" />
                      <p className="text-muted">No website banner available</p>
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="2">
                <div className="text-center">
                  {selectedBanner?.mobile ? (
                    <div className="banner-full-view">
                      <img
                        src={selectedBanner.mobile}
                        alt="Mobile Banner"
                        style={{
                          width: "100%",
                          maxWidth: "600px",
                          height: "auto",
                          border: "2px solid #28a745",
                          borderRadius: "8px",
                          aspectRatio: "16/9",
                          objectFit: "cover"
                        }}
                      />
                      <div className="mt-3">
                        <small className="text-muted">
                          <Smartphone size={14} className="me-1" />
                          Mobile Banner - Aspect Ratio: 16:9
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <Smartphone size={48} className="text-muted mb-3" />
                      <p className="text-muted">No mobile banner available</p>
                    </div>
                  )}
                </div>
              </TabPane>
            </TabContent>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeBannerModal}>
              Close
            </Button>
            {activeTab === '1' && selectedBanner?.website && (
              <Button
                color="primary"
                onClick={() => window.open(selectedBanner.website, '_blank')}
              >
                <Monitor size={14} className="me-1" />
                Open Website Banner
              </Button>
            )}
            {activeTab === '2' && selectedBanner?.mobile && (
              <Button
                color="success"
                onClick={() => window.open(selectedBanner.mobile, '_blank')}
              >
                <Smartphone size={14} className="me-1" />
                Open Mobile Banner
              </Button>
            )}
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default SpecialTable;